import { useMemo } from "react";
import { createClient, createConfig } from "../generated-api/account/client";
import { acquireCoreToken, deviceManagerScopes } from "../utils/auth";
import useCoreContext from "@gf-core/widgets/hooks/useCoreContext";
import { datadogRum } from "@datadog/browser-rum";

export const useDeviceClient = () => {
  const { applicationContext, LIB_VERSION } = useCoreContext();

  const client = useMemo(() => {
    const instance = createClient(
      createConfig({
        baseUrl: import.meta.env.VITE_API_BASE_DEVICE_URL,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Application-Context": `${applicationContext},${LIB_VERSION}`,
        },
        auth: () => acquireCoreToken(deviceManagerScopes),
      }),
    );

    // Catch empty responses where no HTTP response reaches the client
    // (CORS, network failures, VPNs or ad-blockers).
    instance.interceptors.error.use((error, response, request) => {
      if (response === undefined) {
        datadogRum.addError(error, {
          errorType: "request_failed_without_response",
          environment: import.meta.env.VITE_ENVIRONMENT,
          applicationContext,
          requestUrl: request?.url,
          requestMethod: request?.method,
          requestApplicationContext: request?.headers.get("X-Application-Context"),
        });
        throw {
          title: "Couldn't reach our servers",
          detail:
            "We couldn't connect to our servers. This can be caused by your network, a VPN, browser privacy settings or ad-blockers. Try again, and if the problem continues contact your IT department for assistance.",
          titleLocalizationKey: "coreGeneral.blockedErrorTitle",
          detailLocalizationKey: "coreGeneral.blockedErrorDescription",
          traceId: request?.headers.get("X-Datadog-Trace-Id") ?? undefined,
        };
      }

      return error;
    });

    return instance;
  }, [applicationContext]);

  return client;
};
